import { isNullOrUndefined } from 'rewire-common';
import {
  createWatcherFn,
  WatcherTypeFn,
  version
} from 'rewire-core';
import { GQL } from 'rewire-graphql';
import {
  IUdf,
  ISubscriptionRecord,
  IUdfDocumentTypes,
  UdfDocumentType,
  UdfFilter
} from '../DataTypes';
import DataQueries from '../graphql/Queries';
import gql from 'graphql-tag';
import EntitySubscriptionService, {
  EntitySubscriptionModel,
  SubscriptionCallbackFn,
} from './EntitySubscriptionService';
import { subscribe } from '../graphql/GraphQLClient';

const udfsSubscription = (returnSchema: string) => gql`
  subscription {
    udfs {
      udf {
        ${returnSchema}
      }
      operation
    }
  }
`;

export function subscribeToUdfs(returnSchema: string, callback: any): any {
  return subscribe(udfsSubscription(returnSchema) as GQL, undefined, callback, 'udfs');
}

export type UdfSort = (u1: IUdf, u2: IUdf) => number;

export function defaultUdfSort(u1: IUdf, u2: IUdf) {
  return u1.displayName.localeCompare(u2.displayName);
}

export const defaultUdfSchema = `
  id
  documentType
  type
  name
  isSystem
  isList
  hasEffective
  displayName
  allowableValues
`;

interface IUdfSubscriptionModelState {
  builtInUdfs: IUdf[];
  udfDocumentTypes: IUdfDocumentTypes;
  udfsByDocumentTypeMap: { [key: string]: IUdf[] };
}

export class UdfsSubscriptionModel extends EntitySubscriptionModel<IUdf, IUdfSubscriptionModelState> {
  watchUdfs: WatcherTypeFn;
  private _initialized: boolean;

  constructor(options?: { returnSchema?: string, externalEntitiesContainer?: IUdf[], subscriptionCallback?: SubscriptionCallbackFn<IUdf> }) {
    super({ returnSchema: options?.returnSchema || defaultUdfSchema, filter: {}, sort: defaultUdfSort, externalEntitiesContainer: options?.externalEntitiesContainer, subscriptionCallback: options?.subscriptionCallback });

    this._initialized = false;
    this._state.builtInUdfs = [];
    this._state.udfDocumentTypes = {};
    this._state.udfsByDocumentTypeMap = {};

    this.watchUdfs = createWatcherFn();
    this.watchUdfs(() => { version(this.udfs); }, () => {
      const newUdfsByDocumentTypeMap: Record<string, IUdf[]> = {};
      for (const udf of this.udfs) {
        if (!newUdfsByDocumentTypeMap[udf.documentType]) {
          newUdfsByDocumentTypeMap[udf.documentType] = [udf];
        } else {
          newUdfsByDocumentTypeMap[udf.documentType].push(udf);
        }
      }

      for (const documentType of Object.keys(this.udfsByDocumentTypeMap) as UdfDocumentType[]) {
        this.udfsByDocumentTypeMap[documentType].length = 0;
        if (newUdfsByDocumentTypeMap[documentType]?.length) {
          this.udfsByDocumentTypeMap[documentType].push(...newUdfsByDocumentTypeMap[documentType]);
        }
      }
    });
  }

  async _initialize() {
    const udfDocumentTypes = await DataQueries.getUdfDocumentTypes();
    if (!udfDocumentTypes) return;
    this.udfDocumentTypes = udfDocumentTypes;
    for (const udfDocumentType of Object.keys(udfDocumentTypes)) {
      this._state.udfsByDocumentTypeMap[udfDocumentType] = [];
    }
  }

  set udfs(value: IUdf[]) {
    this.entities = value;
  }

  get udfs(): IUdf[] {
    return this.entities;
  }

  get udfCount(): number {
    return this.entityCount;
  }

  set builtInUdfs(value: IUdf[]) {
    this._state.builtInUdfs.length = 0;
    this._state.builtInUdfs.push(...value);
  }

  get builtInUdfs(): IUdf[] {
    return this._state.builtInUdfs;
  }

  set udfDocumentTypes(value: IUdfDocumentTypes) {
    this._state.udfDocumentTypes = value;
  }

  get udfDocumentTypes(): IUdfDocumentTypes {
    return this._state.udfDocumentTypes;
  }

  get udfsByDocumentTypeMap(): { [key: string]: IUdf[] } {
    return this._state.udfsByDocumentTypeMap;
  }

  getUdfDocumentType(udfName: string): UdfDocumentType | undefined {
    const udf = this.udfs.find(u => u.name === udfName);
    return udf?.documentType;
  }

  getUdfByName(name: string): IUdf | undefined {
    return this.udfs.find(u => u.name === name);
  }

  getUdfsByDocumentType(documentType?: UdfDocumentType): IUdf[] {
    if (!documentType) {
      return this.udfs;
    }
    return this.udfsByDocumentTypeMap[documentType] || [];
  }

  getUdfsByIds(ids: string[]): IUdf[] {
    return this.udfs.filter(u => ids.some(id => id === u.id));
  }

  getUdfs(filter?: Omit<UdfFilter, 'locationId' | 'tags' | 'search' | 'custom'>): IUdf[] {
    let udfs = this.udfs;
    if (filter) {
      udfs = udfs.filter(udf => (
        Object.keys(filter).every((filterKey: keyof typeof filter) => {
          const filterValue = filter[filterKey];
          const udfValue = udf[filterKey];
          return isNullOrUndefined(filterValue) || (Array.isArray(filterValue) ? filterValue.includes(udfValue as string) : udfValue === filterValue);
        })
      ));
    }

    return udfs;
  }

  getUdfFieldNames(documentTypes?: UdfDocumentType[]): string[] {
    const filter = documentTypes?.length ? { documentType: documentTypes } : undefined;
    const udfs = this.getUdfs(filter);
    return udfs.map((u: IUdf) => u.name);
  }

  getBuiltInUdfs(documentType?: UdfDocumentType): IUdf[] {
    return this.builtInUdfs.filter(bu => !documentType || bu.documentType === documentType);
  }

  getBuiltInUdfsByIds(ids: string[]): IUdf[] {
    return this.builtInUdfs.filter(bu => ids.some(id => bu.id === id));
  }

  getBuiltInUdfsNotInUse(documentType?: UdfDocumentType): IUdf[] {
    const builtInUdfs = this.getBuiltInUdfs(documentType);
    const currUdfs = this.getUdfsByDocumentType(documentType);
    return builtInUdfs.filter(bu => !currUdfs.some(cu => cu.name === bu.name));
  }

  protected async _getEntities(returnSchema: string): Promise<IUdf[]> {
    const udfs = [];
    let result = undefined;
    let cursor = undefined;
    do {
      result = await DataQueries.getUdfs(returnSchema, undefined, undefined, undefined, 200, cursor);
      cursor = result?.cursor;
      udfs.push(...(result?.data ?? []) as IUdf[]);
    } while (cursor);

    return udfs;
  }

  protected async _getBuiltInUdfs(): Promise<IUdf[]> {
    const result = await DataQueries.getBuiltInUdfs(this.returnSchema);
    this.sort && result?.sort(this.sort);
    return (result ?? []) as IUdf[];
  }

  protected subscribe(returnSchema: string, filter: Record<string, unknown>, callback: any): any {
    return subscribeToUdfs(returnSchema, callback);
  }

  protected mergeAccessor(udfRecord: ISubscriptionRecord<IUdf>): IUdf {
    return udfRecord['udf'] as IUdf;
  }

  public async startUpdate(): Promise<boolean> {
    if (!this._initialized) {
      await this._initialize();
    }
    if (await super.startUpdate()) {
      this.builtInUdfs = await this._getBuiltInUdfs();
      return true;
    }
    return false;
  }
}

class UdfsSubscriptionService extends EntitySubscriptionService<IUdf, UdfsSubscriptionModel> {
  constructor() {
    super();
    this.createSubscriptionModel();
  }

  get subscriptionModel(): UdfsSubscriptionModel {
    return this.subscriptionModels[0] as UdfsSubscriptionModel;
  }

  createSubscriptionModel(options?: { returnSchema?: string, externalEntitiesContainer?: IUdf[], subscriptionCallback?: SubscriptionCallbackFn<IUdf> }): UdfsSubscriptionModel {
    if (!this.subscriptionModels.length) {
      super.createSubscriptionModel(options);
    }

    return this.subscriptionModel;
  }

  protected _createSubscriptionModel(options?: { returnSchema?: string, externalEntitiesContainer?: IUdf[], subscriptionCallback?: SubscriptionCallbackFn<IUdf> }): UdfsSubscriptionModel {
    return new UdfsSubscriptionModel(options);
  }
}

const udfsSubscriptionService = new UdfsSubscriptionService();
export default udfsSubscriptionService;
